import { useState } from 'react';
import { Button, Flex, Input, Popover, Tooltip } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { DatePicker, DateValue } from '@mantine/dates';
import { useTranslationMipMessages } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { AppliedFilter } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/AppliedFilter/AppliedFilter';
import { INVESTOR_MESSAGES_TEST_ID } from '@dealstack/shared/tests/testElementIds';
import { DateTime } from 'luxon';

interface Props {
	searchText: string;
	selectedDates: [DateValue, DateValue];
	dateOfFirstMessage: Date | undefined;
	onSearchQueryChange: (searchQuery: string) => void;
	onDateRangeChange: (dateRange: [DateValue, DateValue]) => void;
}

export const MessagesFilter = ({
	searchText,
	selectedDates,
	onSearchQueryChange,
	onDateRangeChange,
	dateOfFirstMessage,
}: Props) => {
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const tMipMessages = useTranslationMipMessages();

	const hasSelectedDates = (selectedDates: [DateValue, DateValue]) =>
		!!(selectedDates[0] && selectedDates[1]);

	const handleDateChange = (newDate: [DateValue, DateValue]) => {
		onDateRangeChange(newDate);

		const areBothDatesSelected = newDate[0] && newDate[1];
		if (areBothDatesSelected) {
			setIsDatePickerOpen(false);
		}
	};

	const selectedDatesText = `${selectedDates[0]?.toDateString?.() ?? ''} - ${selectedDates[1]?.toDateString?.() ?? ''}`;

	// We do this because we do not want to exclude the date of the first message from the date picker
	const dateOfFirstMessageLessADay = DateTime.fromJSDate(new Date(dateOfFirstMessage ?? ''))
		.minus({ day: 1 })
		.toJSDate();

	return (
		<>
			<Flex gap="0.3rem" mb="xs" align="center">
				<Input
					data-testid={INVESTOR_MESSAGES_TEST_ID.MESSAGE_SEARCH_INPUT}
					placeholder={tMipMessages('list.search')}
					styles={{
						root: { margin: '0px' },
						wrapper: { margin: '0px', width: '100%' },
						input: { margin: '0' },
					}}
					leftSection={<Icon variant="search" size="lg" />}
					size="md"
					value={searchText}
					onChange={(e) => onSearchQueryChange(e.target.value)}
				/>
				<Popover
					opened={isDatePickerOpen}
					onChange={setIsDatePickerOpen}
					position="bottom-start"
					shadow="md"
				>
					<Popover.Target>
						<Button
							type="button"
							variant="default"
							size="lg"
							styles={{
								root: {
									padding: '0 0.6rem',
								},
							}}
							onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
						>
							<Icon variant="calendar" size="lg"></Icon>
						</Button>
					</Popover.Target>
					<Popover.Dropdown>
						<DatePicker
							type="range"
							excludeDate={(date) => {
								return date < dateOfFirstMessageLessADay;
							}}
							allowSingleDateInRange
							value={selectedDates}
							onChange={handleDateChange}
							getDayProps={(date) => {
								const messagesFeatureReleaseDate = DateTime.fromJSDate(
									new Date('2025-08-13')
								)
									.minus({ day: 1 })
									.toJSDate();
								const isBeforeMessagesFeatureWasReleased =
									date < messagesFeatureReleaseDate;

								if (isBeforeMessagesFeatureWasReleased) {
									return {
										disabled: true,
										renderDay: (day) => (
											<Tooltip
												label={tMipMessages(
													'list.date_before_messages_feature_released'
												)}
												withArrow
											>
												<div>{day.getDate()}</div>
											</Tooltip>
										),
									};
								}

								const isBeforeExcludeDate = date < dateOfFirstMessageLessADay;
								if (isBeforeExcludeDate) {
									return {
										disabled: true,
										renderDay: (day) => (
											<Tooltip
												label={tMipMessages(
													'list.date_before_first_message'
												)}
												withArrow
											>
												<div>{day.getDate()}</div>
											</Tooltip>
										),
									};
								}

								return {};
							}}
						/>
					</Popover.Dropdown>
				</Popover>
			</Flex>
			{hasSelectedDates(selectedDates) && (
				<Flex>
					<AppliedFilter
						text={selectedDatesText}
						onClose={() => {
							onDateRangeChange([null, null]);
						}}
					/>
				</Flex>
			)}
		</>
	);
};
